/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import { useAuthInfo } from "@propelauth/react";
import { useMutation } from "@tanstack/react-query";
import React, {
  useMemo, useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { queryClient, useViews } from "../../../hooks/QueryHooks";
import { createView } from "../../../services/view/viewEndpoints";
import ViewIcon from "../../../shared/ui/icons/IconPicker/ViewIcon";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import TypeSelect from "./TypeSelect";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Checkbox } from "../../../components/ui/checkbox";
import { Button } from "../../../components/ui/button";

type Props = {
  open: boolean;
  setOpen:
  | ((value: boolean) => void)
  | (((value: boolean) => void) & React.FormEvent<HTMLDivElement>);
  defaultType?: string;
  defaultName?: string;
  disableType?: boolean;
  onClick?: any;
};

const CreateView = ({
  onClick,
  open,
  setOpen,
  defaultType,
  defaultName,
  disableType = false,
}: Props) => {
  const [name, setName] = useState<string>(defaultName || "");
  const [description, setDescription] = useState<string>("");
  const [type, setType] = useState<any>(defaultType || "");
  const [isPublic, setIsPublic] = useState<boolean>(false);
  const [selectedIcon, setSelectedIcon] = useState<{ type: 'emoji' | 'built_in', value: string } | undefined>();
  const [iconHovered, setIconHovered] = useState<boolean>(false);
  const currentUser = useAuthInfo().user;
  const currentWorkspace = useWorkspaceStore((state) => state.workspace);
  const navigate = useNavigate();

  const { data: views } : any = useViews({
    workspaceId: currentWorkspace?.id,
    enabled: !!currentWorkspace?.id,
  });

  const createViewMutation = useMutation({
    mutationFn: createView,
  });

  const accountType = useMemo(
    () => ({
      ...views?.filter((item: any) => item?.is_precreated)?.find((val: any) => val?.entity_relation === "accounts"),
      owner_id: currentUser?.userId,
      workspace_id: currentWorkspace?.id,
      is_precreated: false,
    }),
    [currentUser?.userId, currentWorkspace?.id, views],
  );

  const contactType = useMemo(
    () => ({
      ...views?.filter((item: any) => item?.is_precreated)?.find((val: any) => val?.entity_relation === "users"),
      owner_id: currentUser?.userId,
      workspace_id: currentWorkspace.id,
      is_precreated: false,
    }),
    [currentUser?.userId, currentWorkspace.id, views],
  );

  const onTypeClick = (select: string) => {
    setType(select);

    if (select === "accounts" && !selectedIcon) {
      setSelectedIcon({ type: 'built_in', value: 'Building' });
    } else if (select === "users" && !selectedIcon) {
      setSelectedIcon({ type: 'built_in', value: 'Users' });
    }
  };

  const submitHandler = () => {
    if (currentWorkspace.id) {
      let viewData: any;

      const iconData = selectedIcon ? {
        icon_type: selectedIcon.type,
        icon_key: selectedIcon.value,
        view_icon: selectedIcon.value,
        icon_asset_id: undefined
      } : {};

      if (type === "accounts") {
        const { icon_type: iconType, icon_key: iconKey, view_icon: viewIcon, ...cleanAccountType } = accountType;

        viewData = {
          ...cleanAccountType,
          view_description: description.trim() === '' ? null : description,
          view_name: name,
          is_public: isPublic,
          is_precreated: false,
          filters: {},
          ...iconData
        };
      } else if (type === "users") {
        const { icon_type: iconType, icon_key: iconKey, view_icon: viewIcon, ...cleanContactType } = contactType;

        viewData = {
          ...cleanContactType,
          view_name: name,
          view_description: description.trim() === '' ? null : description,
          is_public: isPublic,
          is_precreated: false,
          filters: {},
          ...iconData
        };
      }

      const currentIconData = {
        type: selectedIcon?.type,
        value: selectedIcon?.value
      };

      createViewMutation.mutate(viewData, {
        onSuccess: (data: any) => {
          queryClient.setQueryData(["views", currentWorkspace?.id], (oldData: any) => {
            if (!oldData) return oldData;

            const newView = {
              ...data?.data,
              owner_id: currentUser?.userId,
              icon_type: currentIconData.type,
              icon_key: currentIconData.value,
              view_icon: currentIconData.value
            };

            return {
              ...oldData,
              data: {
                ...oldData.data,
                views: [...oldData.data.views, newView],
              },
            };
          });

          queryClient.invalidateQueries(["views", currentWorkspace?.id]);
          if (import.meta.env.VITE_HE_WORKSPACE_KEY && import.meta.env.VITE_HE_DATA_KEY) {
            window?.hyperengage(
              "track",
              "new_view_created",
              {
                properties: {
                  view_name: name,
                  view_description: description,
                  is_public: isPublic,
                  created_by: currentUser?.userId,
                  workspace_id: currentWorkspace?.id,
                  entity_type: type,
                  has_icon: !!selectedIcon,
                },
              },
            );
          }
          toast.success("New View Created", {
            description: "You have successfully created a new view.",
          });
          setTimeout(() => {
            navigate(`/home/${currentWorkspace?.unique_identifier}/view/${data?.data.id}`);
          }, 100);
        },
        onError: (error: any) => {
          if (error.response?.status !== 0) {
            toast.error("Unknown error.", {
              description: "An unknown error occurred while creating the new view. Please try again later or contact our support team for assistance.",
            });
          }
        },
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => setOpen(value)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New View</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex gap-6 items-end">
            <div className="flex flex-col items-center gap-2">
              <div
                className="relative cursor-pointer"
                onMouseEnter={() => setIconHovered(true)}
                onMouseLeave={() => setIconHovered(false)}
              >
                <ViewIcon
                  selectedIcon={selectedIcon}
                  onSelect={setSelectedIcon}
                  size={40}
                  className={`flex-shrink-0 transition-all duration-200 ${iconHovered ? 'scale-105 shadow-md' : ''}`}
                />
              </div>
            </div>
            <div className="flex-grow space-y-1.5">
              <Label htmlFor="viewName">View name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                id="viewName"
                type="text"
                placeholder="Enter view name"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="view_description">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              id="view_description"
              placeholder="Add a description for your view"
              rows={4}
            />
          </div>

          <div className="flex w-full items-center justify-between gap-4">
            <TypeSelect
              selected={type === "accounts"}
              disabled={defaultType === "users" && disableType}
              onClick={onTypeClick}
              type="accounts"
            />
            <TypeSelect
              selected={type === "users"}
              disabled={defaultType === "accounts" && disableType}
              onClick={onTypeClick}
              type="users"
            />
          </div>

          <div className="flex items-center gap-2 px-1">
            <Checkbox
              checked={isPublic}
              onCheckedChange={(checked) => setIsPublic(Boolean(checked))}
              id="isPublic"
            />
            <Label htmlFor="isPublic" className="text-sm font-normal cursor-pointer">
              Do you want your view to be public?
            </Label>
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Back
          </Button>
          <Button
            disabled={!type || !name}
            onClick={() => {
              if (onClick) {
                onClick(name, type, isPublic);
                setOpen(false);
              } else {
                submitHandler();
              }
            }}
          >
            Create view
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateView;
